import type { AnimationEvent } from "@/types"

/**
 * Maps action types (from detailHeader.action_type) to oklch color strings.
 * These colors provide visual feedback during replay playback.
 *
 * action_type values come from MatchEvent#detail_header:
 *   self.class.name.demodulize.sub("Event", "")
 * e.g. MoveEvent → "Move", BlockEvent → "Block", etc.
 */
export const ACTION_COLORS: Record<string, string> = {
  Move: "oklch(0.65 0.03 80)", // neutral (warm gray, per design spec --action-move)
  Block: "oklch(0.60 0.22 28)", // red
  Blitz: "oklch(0.70 0.18 55)", // orange
  Pass: "oklch(0.60 0.15 250)", // blue
  Dodge: "oklch(0.60 0.18 160)", // brighter green
  Foul: "oklch(0.55 0.15 310)", // purple
  Handoff: "oklch(0.60 0.15 200)", // teal
  ThrowTeammate: "oklch(0.60 0.14 270)", // indigo
  Touchdown: "oklch(0.78 0.18 55)", // gold
  Turnover: "oklch(0.55 0.18 28)", // dark red
  Kickoff: "oklch(0.58 0.10 220)", // blue-gray
}

/**
 * Badge styles for narrative panel action pills.
 * Each entry has bg (background), text (text color), and border (border color).
 */
export interface ActionBadgeStyle {
  bg: string
  text: string
  border: string
}

export const ACTION_BADGE_STYLES: Record<string, ActionBadgeStyle> = {
  Move: {
    bg: "oklch(0.22 0.02 80 / 0.3)",
    text: "oklch(0.65 0.03 80)",
    border: "oklch(0.35 0.03 80)",
  },
  Block: {
    bg: "oklch(0.25 0.10 28 / 0.4)",
    text: "oklch(0.60 0.22 28)",
    border: "oklch(0.40 0.15 28)",
  },
  Blitz: {
    bg: "oklch(0.25 0.10 55 / 0.4)",
    text: "oklch(0.70 0.18 55)",
    border: "oklch(0.45 0.14 55)",
  },
  Pass: {
    bg: "oklch(0.22 0.08 250 / 0.4)",
    text: "oklch(0.60 0.15 250)",
    border: "oklch(0.40 0.12 250)",
  },
  Dodge: {
    bg: "oklch(0.22 0.08 145 / 0.4)",
    text: "oklch(0.60 0.18 160)",
    border: "oklch(0.40 0.12 145)",
  },
  Foul: {
    bg: "oklch(0.22 0.08 310 / 0.4)",
    text: "oklch(0.55 0.15 310)",
    border: "oklch(0.40 0.12 310)",
  },
  Handoff: {
    bg: "oklch(0.22 0.08 200 / 0.4)",
    text: "oklch(0.60 0.15 200)",
    border: "oklch(0.40 0.12 200)",
  },
  ThrowTeammate: {
    bg: "oklch(0.22 0.08 270 / 0.4)",
    text: "oklch(0.60 0.14 270)",
    border: "oklch(0.40 0.12 270)",
  },
  Touchdown: {
    bg: "oklch(0.25 0.10 55 / 0.3)",
    text: "oklch(0.78 0.18 55)",
    border: "oklch(0.50 0.15 55)",
  },
  Turnover: {
    bg: "oklch(0.25 0.10 28 / 0.3)",
    text: "oklch(0.55 0.18 28)",
    border: "oklch(0.40 0.15 28)",
  },
  Kickoff: {
    bg: "oklch(0.22 0.06 220 / 0.4)",
    text: "oklch(0.58 0.10 220)",
    border: "oklch(0.40 0.08 220)",
  },
}

const DEFAULT_BADGE_STYLE: ActionBadgeStyle = {
  bg: "oklch(0.16 0.005 230 / 0.4)",
  text: "oklch(0.65 0.02 80)",
  border: "oklch(0.25 0.01 230)",
}

/**
 * Returns badge pill styles (bg/text/border) for a given action type name.
 * Falls back to neutral styling for unknown action types.
 */
export function getActionBadgeStyle(
  actionType: string | null | undefined,
): ActionBadgeStyle {
  if (actionType && actionType in ACTION_BADGE_STYLES) {
    return ACTION_BADGE_STYLES[actionType]
  }
  return DEFAULT_BADGE_STYLE
}

/**
 * Returns the left-border highlight color for a narrative entry's action type.
 * Uses the main ACTION_COLORS map (matching the pitch highlight color).
 */
export function getActionBorderColor(
  actionType: string | null | undefined,
): string {
  if (actionType && actionType in ACTION_COLORS) {
    return ACTION_COLORS[actionType]
  }
  return "transparent"
}

/**
 * Returns the highlight color for a given animation event.
 * Returns null when no special color applies.
 */
export function getActionColor(
  event: AnimationEvent | null | undefined,
): string | null {
  if (!event) return null

  const header = event.detailHeader
  if (
    typeof header === "object" &&
    header !== null &&
    "action_type" in header
  ) {
    const actionType = header.action_type
    if (actionType && actionType in ACTION_COLORS) {
      return ACTION_COLORS[actionType]
    }
  }

  // Fallback: map highlightType to a color
  if (event.highlightType === "touchdown") return ACTION_COLORS.Touchdown
  if (event.highlightType === "turnover") return ACTION_COLORS.Turnover
  if (event.highlightType === "kickoff") return ACTION_COLORS.Kickoff

  return null
}
